import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChefHat, LogOut, User, ListOrdered, Receipt, Loader2, Plus, Trash2,
  Pencil, X, Check, Image as ImageIcon, Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import api from "@/lib/api";

const STATUS_LABELS = {
  pending: "Pending", confirmed: "Confirmed", preparing: "Preparing",
  out_for_delivery: "Out for Delivery", delivered: "Delivered", cancelled: "Cancelled",
};

export default function RestaurantDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [tab, setTab] = useState("profile");

  const doLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
              <ChefHat className="w-5 h-5" />
            </div>
            <div>
              <p className="font-serif font-bold text-lg leading-tight">{user?.name || "Restaurant"}</p>
              <p className="text-xs text-muted-foreground">Restaurant Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/")} className="text-sm" data-testid="rp-view-site">
              View site
            </Button>
            <Button variant="ghost" size="icon" onClick={doLogout} title="Sign out" data-testid="rp-logout">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-3 max-w-md">
            <TabsTrigger value="profile" data-testid="rp-tab-profile">
              <User className="w-4 h-4 mr-1.5" />Profile
            </TabsTrigger>
            <TabsTrigger value="menu" data-testid="rp-tab-menu">
              <ListOrdered className="w-4 h-4 mr-1.5" />Menu
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="rp-tab-orders">
              <Receipt className="w-4 h-4 mr-1.5" />Orders
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-6">
            <ProfileTab toast={toast} />
          </TabsContent>
          <TabsContent value="menu" className="mt-6">
            <MenuTab toast={toast} />
          </TabsContent>
          <TabsContent value="orders" className="mt-6">
            <OrdersTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ---------------- Profile ----------------
function ProfileTab({ toast }) {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/restaurant/me").then(({ data }) => setProfile(data)).catch(() => setProfile(null));
  }, []);

  if (!profile) return <Loader />;

  const upd = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  const save = async (overrides = {}) => {
    setSaving(true);
    try {
      const payload = {
        name: profile.name, phone: profile.phone, cuisine: profile.cuisine,
        address: profile.address, image_url: profile.image_url,
        delivery_time: Number(profile.delivery_time),
        delivery_fee: Number(profile.delivery_fee),
        is_open: profile.is_open,
        ...overrides,
      };
      const { data } = await api.patch("/restaurant/me", payload);
      setProfile(data);
      toast({ title: "Profile updated" });
    } catch (e) {
      toast({ title: "Update failed", description: e?.response?.data?.detail, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "Image too large (max 2MB)", variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setProfile({ ...profile, image_url: reader.result });
    reader.readAsDataURL(file);
  };

  return (
    <div className="bg-card border rounded-2xl p-6 max-w-3xl space-y-6">
      <div className="flex items-start gap-4">
        <div className="w-24 h-24 rounded-2xl bg-muted overflow-hidden border shrink-0 relative">
          {profile.image_url ? (
            <img src={profile.image_url} alt={profile.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <ImageIcon className="w-8 h-8" />
            </div>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <Label className="cursor-pointer">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-medium hover:bg-muted transition-colors">
              <ImageIcon className="w-3.5 h-3.5" /> Upload cover photo
            </span>
            <input type="file" accept="image/*" onChange={handleImage} className="hidden"
              data-testid="rp-profile-image-input" />
          </Label>
          <p className="text-xs text-muted-foreground">Square, 1MB+. Shown on your restaurant card.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Restaurant name">
          <Input value={profile.name || ""} onChange={upd("name")} data-testid="rp-field-name" />
        </Field>
        <Field label="Cuisine">
          <Input value={profile.cuisine || ""} onChange={upd("cuisine")} data-testid="rp-field-cuisine" />
        </Field>
        <Field label="Phone">
          <Input value={profile.phone || ""} onChange={upd("phone")} data-testid="rp-field-phone" />
        </Field>
        <Field label="Address">
          <Input value={profile.address || ""} onChange={upd("address")} data-testid="rp-field-address" />
        </Field>
        <Field label="Delivery time (minutes)">
          <Input type="number" min="5" value={profile.delivery_time || 30} onChange={upd("delivery_time")}
            data-testid="rp-field-delivery-time" />
        </Field>
        <Field label="Delivery fee (USD)">
          <Input type="number" step="0.01" min="0" value={profile.delivery_fee || 0}
            onChange={upd("delivery_fee")} data-testid="rp-field-delivery-fee" />
        </Field>
      </div>

      <div className="flex items-center justify-between p-4 rounded-xl bg-muted/40 border">
        <div>
          <p className="font-semibold">Accepting orders</p>
          <p className="text-sm text-muted-foreground">
            Turn off if you&apos;re closed or fully booked.
          </p>
        </div>
        <Switch
          checked={!!profile.is_open}
          onCheckedChange={(v) => { setProfile({ ...profile, is_open: v }); save({ is_open: v }); }}
          data-testid="rp-toggle-open"
        />
      </div>

      <Button onClick={() => save()} disabled={saving} className="font-semibold" data-testid="rp-profile-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
      </Button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</Label>
      {children}
    </div>
  );
}

// ---------------- Menu ----------------
function MenuTab({ toast }) {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null); // null|new|item
  const [editingCat, setEditingCat] = useState(null);
  const [newCatName, setNewCatName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [cats, mi] = await Promise.all([
        api.get("/restaurant/me/categories"),
        api.get("/restaurant/me/menu-items"),
      ]);
      setCategories(cats.data);
      setItems(mi.data);
    } catch {
      toast({ title: "Failed to load menu", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await api.post("/restaurant/me/categories", {
        name: newCatName.trim(),
        sort_order: categories.length,
      });
      setNewCatName("");
      load();
      toast({ title: "Category added" });
    } catch (e) {
      toast({ title: "Could not add", variant: "destructive" });
    }
  };

  const renameCategory = async (cat, name) => {
    try {
      await api.patch(`/restaurant/me/categories/${cat.id}`, { name, sort_order: cat.sort_order });
      load();
      setEditingCat(null);
    } catch {
      toast({ title: "Could not update", variant: "destructive" });
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}" and all items in it?`)) return;
    try {
      await api.delete(`/restaurant/me/categories/${cat.id}`);
      load();
      toast({ title: "Category deleted" });
    } catch {
      toast({ title: "Could not delete", variant: "destructive" });
    }
  };

  const deleteItem = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try {
      await api.delete(`/restaurant/me/menu-items/${it.id}`);
      load();
      toast({ title: "Item deleted" });
    } catch {
      toast({ title: "Could not delete", variant: "destructive" });
    }
  };

  const toggleAvail = async (it) => {
    try {
      await api.patch(`/restaurant/me/menu-items/${it.id}`, { is_available: !it.is_available });
      load();
    } catch {
      toast({ title: "Could not update", variant: "destructive" });
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="bg-card border rounded-2xl p-4 md:p-6">
        <h3 className="font-serif text-lg font-bold mb-3">Categories</h3>
        <div className="flex gap-2 mb-4">
          <Input
            value={newCatName}
            onChange={(e) => setNewCatName(e.target.value)}
            placeholder="e.g. Signature Jollof"
            onKeyDown={(e) => e.key === "Enter" && addCategory()}
            data-testid="rp-new-category-input"
          />
          <Button onClick={addCategory} className="shrink-0 gap-1.5" data-testid="rp-add-category">
            <Plus className="w-4 h-4" /> Add
          </Button>
        </div>

        <div className="space-y-2">
          {categories.length === 0 && (
            <p className="text-sm text-muted-foreground py-2">No categories yet. Add one to start.</p>
          )}
          {categories.map((c) => (
            <div key={c.id} className="flex items-center gap-2 p-3 rounded-lg border bg-background"
              data-testid={`rp-category-${c.id}`}>
              {editingCat === c.id ? (
                <>
                  <Input
                    defaultValue={c.name}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") renameCategory(c, e.target.value);
                      if (e.key === "Escape") setEditingCat(null);
                    }}
                    autoFocus
                    onBlur={(e) => renameCategory(c, e.target.value)}
                  />
                  <Button size="icon" variant="ghost" onClick={() => setEditingCat(null)}><X className="w-4 h-4" /></Button>
                </>
              ) : (
                <>
                  <span className="flex-1 font-medium">{c.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {items.filter((i) => i.category_id === c.id).length} items
                  </span>
                  <Button size="icon" variant="ghost" onClick={() => setEditingCat(c.id)} data-testid={`rp-edit-cat-${c.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => deleteCategory(c)}
                    className="text-destructive hover:text-destructive" data-testid={`rp-delete-cat-${c.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card border rounded-2xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg font-bold">Menu Items</h3>
          {categories.length > 0 && (
            <Button onClick={() => setEditingItem("new")} className="gap-1.5" data-testid="rp-add-item">
              <Plus className="w-4 h-4" /> Add item
            </Button>
          )}
        </div>

        {categories.length === 0 ? (
          <p className="text-sm text-muted-foreground">Add a category first to create menu items.</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No items yet. Add your first dish!</p>
        ) : (
          <div className="space-y-6">
            {categories.map((c) => {
              const list = items.filter((i) => i.category_id === c.id);
              if (list.length === 0) return null;
              return (
                <div key={c.id}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{c.name}</p>
                  <div className="space-y-2">
                    {list.map((it) => (
                      <div key={it.id} className="flex items-center gap-3 p-3 rounded-lg border bg-background"
                        data-testid={`rp-item-${it.id}`}>
                        {it.image_url ? (
                          <img src={it.image_url} alt={it.name} className="w-14 h-14 rounded-md object-cover bg-muted" />
                        ) : (
                          <div className="w-14 h-14 rounded-md bg-muted flex items-center justify-center">
                            <ImageIcon className="w-5 h-5 text-muted-foreground/50" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium truncate">{it.name}</p>
                            {it.is_popular && (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">
                                <Star className="w-2.5 h-2.5 fill-amber-700 mr-0.5" />Popular
                              </Badge>
                            )}
                          </div>
                          {it.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1">{it.description}</p>
                          )}
                          <p className="text-sm font-semibold text-primary mt-0.5">${it.price?.toFixed?.(2)}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => toggleAvail(it)}
                            className={`text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded border ${
                              it.is_available ? "border-green-300 text-green-700 bg-green-50" : "border-muted text-muted-foreground"
                            }`}
                            data-testid={`rp-item-toggle-${it.id}`}
                          >
                            {it.is_available ? "Available" : "Hidden"}
                          </button>
                          <Button size="icon" variant="ghost" onClick={() => setEditingItem(it)} data-testid={`rp-edit-item-${it.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => deleteItem(it)}
                            className="text-destructive hover:text-destructive" data-testid={`rp-delete-item-${it.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editingItem && (
        <ItemDialog
          item={editingItem === "new" ? null : editingItem}
          categories={categories}
          onClose={() => setEditingItem(null)}
          onSaved={() => { setEditingItem(null); load(); }}
        />
      )}
    </div>
  );
}

function ItemDialog({ item, categories, onClose, onSaved }) {
  const isNew = !item;
  const { toast } = useToast();
  const [form, setForm] = useState(() => ({
    category_id: item?.category_id || categories[0]?.id || "",
    name: item?.name || "",
    description: item?.description || "",
    price: item?.price ?? 0,
    image_url: item?.image_url || "",
    is_popular: !!item?.is_popular,
    is_available: item?.is_available !== false,
  }));
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "Image too large (max 2MB)", variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, image_url: reader.result });
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        category_id: form.category_id, name: form.name,
        description: form.description || null,
        price: Number(form.price),
        image_url: form.image_url || null,
        is_popular: form.is_popular, is_available: form.is_available,
      };
      if (isNew) {
        await api.post("/restaurant/me/menu-items", payload);
      } else {
        await api.patch(`/restaurant/me/menu-items/${item.id}`, payload);
      }
      onSaved();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{isNew ? "New menu item" : "Edit item"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Category</Label>
            <select value={form.category_id} onChange={upd("category_id")}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid="rp-item-category">
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Item name</Label>
            <Input value={form.name} onChange={upd("name")} data-testid="rp-item-name" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={upd("description")} rows={2}
              data-testid="rp-item-description" />
          </div>
          <div className="space-y-2">
            <Label>Price (USD)</Label>
            <Input type="number" step="0.01" min="0" value={form.price} onChange={upd("price")}
              data-testid="rp-item-price" />
          </div>
          <div className="space-y-2">
            <Label>Photo</Label>
            <div className="flex items-center gap-3">
              {form.image_url ? (
                <img src={form.image_url} alt="" className="w-16 h-16 rounded-md object-cover border" />
              ) : (
                <div className="w-16 h-16 rounded-md border flex items-center justify-center bg-muted">
                  <ImageIcon className="w-6 h-6 text-muted-foreground/40" />
                </div>
              )}
              <Label className="cursor-pointer">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-medium hover:bg-muted transition-colors">
                  <ImageIcon className="w-3.5 h-3.5" /> Upload
                </span>
                <input type="file" accept="image/*" onChange={handleImage} className="hidden"
                  data-testid="rp-item-image-input" />
              </Label>
            </div>
          </div>
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-muted/40 border">
            <div>
              <p className="font-medium text-sm">Mark as popular</p>
              <p className="text-xs text-muted-foreground">Shows a &ldquo;Popular&rdquo; badge.</p>
            </div>
            <Switch checked={form.is_popular} onCheckedChange={(v) => setForm({ ...form, is_popular: v })}
              data-testid="rp-item-popular" />
          </div>
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-muted/40 border">
            <div>
              <p className="font-medium text-sm">Available</p>
              <p className="text-xs text-muted-foreground">Off-hours? Hide it temporarily.</p>
            </div>
            <Switch checked={form.is_available} onCheckedChange={(v) => setForm({ ...form, is_available: v })}
              data-testid="rp-item-available" />
          </div>
        </div>
        <div className="flex gap-2 justify-end pt-2 border-t">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.name || !form.category_id}
            data-testid="rp-item-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isNew ? "Add item" : "Save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Orders ----------------
function OrdersTab() {
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/restaurant/me/orders").then(({ data }) => setOrders(data))
      .catch(() => setOrders([])).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (!orders || orders.length === 0) {
    return (
      <div className="bg-card border rounded-2xl p-12 text-center text-muted-foreground">
        <Receipt className="w-12 h-12 mx-auto mb-3 opacity-20" />
        <p className="font-medium">No orders yet</p>
        <p className="text-sm mt-1">When customers order from you, they&apos;ll show up here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {orders.map((o) => (
        <div key={o.id} className="bg-card border rounded-2xl p-4 md:p-5" data-testid={`rp-order-${o.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold">#{o.id.slice(0, 8).toUpperCase()}</p>
              <p className="text-xs text-muted-foreground">
                {new Date(o.created_at).toLocaleString("en-GB", {
                  month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                })}
              </p>
            </div>
            <div className="text-right">
              <Badge className="text-[11px] font-semibold">{STATUS_LABELS[o.status] || o.status}</Badge>
              <p className="font-bold mt-1">${o.total?.toFixed?.(2)}</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t space-y-1">
            {(o.items || []).map((it, idx) => (
              <div key={idx} className="text-sm flex justify-between">
                <span>{it.quantity}× {it.menu_item_name}</span>
                <span className="text-muted-foreground">${it.subtotal?.toFixed?.(2)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
            Deliver to: <span className="text-foreground">{o.delivery_address}</span>
            {o.notes && <p className="mt-1">Notes: {o.notes}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function Loader() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
    </div>
  );
}
